import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ReturnDetail, ReturnsService, ReturnStatus } from '../returns.service';

type ActionState = 'idle' | 'approving' | 'rejecting' | 'escalating' | 'cancelling';

/**
 * D4 — drill-down detail + reconcile actions.
 *
 * Two-column layout:
 *   Left  — original bill snapshot (read-only mirror of what printed)
 *           with returned items highlighted.
 *   Right — return details: refund + items + tender split, then the
 *           audit timeline, then the action button row.
 *
 * Manager actions:
 *   • Approve  → status COMPLETED, drainer noop (stock already adjusted)
 *   • Reject   → status REJECTED, requires note
 *   • Escalate → leaves status, adds note for the next tier
 *   • Cancel   → DRAFT / PENDING only — admin / store-manager
 *
 * Dismissing the modal returns false (no list refresh); confirming any
 * action returns true (caller refreshes the inbox counts + list).
 */
@Component({
  selector: 'app-return-detail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './return-detail.component.html',
  styleUrl: './return-detail.component.scss',
})
export class ReturnDetailComponent implements OnInit {
  @Input({ required: true }) returnId!: string;

  private svc = inject(ReturnsService);
  private toastr = inject(ToastrService);
  activeModal = inject(NgbActiveModal);

  data = signal<ReturnDetail | null>(null);
  isLoading = signal(true);
  actionState = signal<ActionState>('idle');
  noteOpen = signal<null | 'reject' | 'escalate' | 'cancel'>(null);
  noteText = signal('');

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.svc.detail(this.returnId).subscribe({
      next: (row) => {
        this.data.set(row);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toastr.error(err?.error?.message || 'Failed to load return.');
        this.activeModal.dismiss();
      },
    });
  }

  // ── actions ──────────────────────────────────────────────────────

  approve(): void {
    if (this.actionState() !== 'idle') return;
    this.actionState.set('approving');
    this.svc.reconcile(this.returnId, { action: 'APPROVE' }).subscribe({
      next: () => {
        this.toastr.success('Return approved.');
        this.activeModal.close(true);
      },
      error: (err) => {
        this.actionState.set('idle');
        this.toastr.error(err?.error?.message || 'Approval failed.');
      },
    });
  }

  openNote(kind: 'reject' | 'escalate' | 'cancel'): void {
    this.noteOpen.set(kind);
    this.noteText.set('');
  }

  closeNote(): void {
    this.noteOpen.set(null);
    this.noteText.set('');
  }

  submitNote(): void {
    const kind = this.noteOpen();
    if (!kind) return;
    const note = this.noteText().trim();
    if (kind === 'reject' && !note) {
      this.toastr.error('A reason is required to reject a return.');
      return;
    }
    if (kind === 'cancel' && !note) {
      this.toastr.error('A reason is required to cancel a return.');
      return;
    }

    if (kind === 'cancel') {
      this.actionState.set('cancelling');
      this.svc.cancel(this.returnId, note).subscribe({
        next: () => {
          this.toastr.success('Return cancelled.');
          this.activeModal.close(true);
        },
        error: (err) => {
          this.actionState.set('idle');
          this.toastr.error(err?.error?.message || 'Cancel failed.');
        },
      });
      return;
    }

    const action = kind === 'reject' ? 'REJECT' : 'ESCALATE';
    this.actionState.set(kind === 'reject' ? 'rejecting' : 'escalating');
    this.svc.reconcile(this.returnId, { action, note }).subscribe({
      next: () => {
        this.toastr.success(action === 'REJECT' ? 'Return rejected.' : 'Escalated to the next tier.');
        this.activeModal.close(true);
      },
      error: (err) => {
        this.actionState.set('idle');
        this.toastr.error(err?.error?.message || 'Action failed.');
      },
    });
  }

  // ── helpers ──────────────────────────────────────────────────────

  asNumber(v: string | number): number {
    return typeof v === 'number' ? v : Number(v);
  }

  isReconcilable(status: ReturnStatus): boolean {
    return status === 'PENDING_APPROVAL' || status === 'REQUIRES_RECONCILIATION';
  }

  isCancellable(status: ReturnStatus): boolean {
    return status === 'DRAFT' || status === 'PENDING_APPROVAL';
  }

  statusClass(status: ReturnStatus): string {
    switch (status) {
      case 'COMPLETED':
      case 'APPROVED':
        return 'status-ok';
      case 'PENDING_APPROVAL':
        return 'status-pending';
      case 'REQUIRES_RECONCILIATION':
        return 'status-warn';
      case 'REJECTED':
      case 'CANCELLED':
        return 'status-block';
      default:
        return 'status-muted';
    }
  }

  /**
   * Bill item rows highlighted on the left column when they appear in
   * the return items list. Used by the template to draw a side accent.
   */
  isReturnedBillItem(billItemId: string): boolean {
    const ret = this.data();
    if (!ret) return false;
    return ret.items.some((it) => it.originalBillingItem?.id === billItemId);
  }

  returnedQtyFor(billItemId: string): number {
    const ret = this.data();
    if (!ret) return 0;
    return ret.items
      .filter((it) => it.originalBillingItem?.id === billItemId)
      .reduce((sum, it) => sum + it.quantityReturned, 0);
  }
}
